// src/features/data/hygiene-env-tab.tsx
import { DataTable, Kicker, StatusDot, StatusPill } from "@/components/iris";
import type { DataTableColumn } from "@/components/iris";
import { useFixHygiene, useHygieneReport } from "@/hooks/use-data";
import { useDataComparison } from "@/hooks/use-multi-env";
import type { ComparisonMismatch, HygieneIssue } from "@/lib/types";
import { AlertTriangle, CheckCircle2, RefreshCw } from "lucide-react";

// ─── Helpers ───

type SeverityKey = "high" | "medium" | "low";

const SEV_VARIANT: Record<SeverityKey, "err" | "warn" | "idle"> = {
  high: "err",
  medium: "warn",
  low: "idle",
};

// ─── Env Comparison Section ───

function EnvComparisonSection() {
  const { data: comparison, isLoading, refetch, isFetching } = useDataComparison();

  if (isLoading) {
    return (
      <div className="iris-card" style={{ padding: "24px 18px" }}>
        <div style={{ color: "var(--iris-fg-3)", fontSize: 12 }}>Loading comparison...</div>
      </div>
    );
  }

  if (comparison?.error) {
    return (
      <div
        style={{
          borderRadius: "var(--iris-r-lg)",
          border: "1px solid rgba(255,77,210,0.2)",
          background: "rgba(255,77,210,0.06)",
          padding: "16px 18px",
          color: "var(--iris-neon-pink)",
          fontSize: 12,
        }}
      >
        Comparison unavailable: {comparison.error}
      </div>
    );
  }

  const counts = comparison?.counts;
  const mismatches = comparison?.mismatches ?? [];
  const totalMismatches = comparison?.total_mismatches ?? 0;

  const mismatchColumns: DataTableColumn<ComparisonMismatch>[] = [
    {
      key: "name",
      label: "Name",
      render: (m) => (
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {m.name}
        </span>
      ),
    },
    {
      key: "type",
      label: "Type",
      cellVariant: "muted",
    },
    {
      key: "in_prod",
      label: "In Prod",
      render: (m) =>
        m.in_prod ? (
          <CheckCircle2 size={13} style={{ color: "var(--iris-neon-green)" }} />
        ) : (
          <span style={{ color: "var(--iris-fg-4)" }}>{"\u2014"}</span>
        ),
    },
    {
      key: "in_dev",
      label: "In Dev",
      render: (m) =>
        m.in_dev ? (
          <CheckCircle2 size={13} style={{ color: "var(--iris-env-dev)" }} />
        ) : (
          <span style={{ color: "var(--iris-fg-4)" }}>{"\u2014"}</span>
        ),
    },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {/* Section header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Kicker>Environment Comparison</Kicker>
        <button
          type="button"
          className="iris-btn"
          onClick={() => refetch()}
          disabled={isFetching}
          style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
        >
          <RefreshCw size={12} className={isFetching ? "iris-pulse" : ""} />
          Refresh
        </button>
      </div>

      {/* Sync cards */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        {/* Groups card */}
        <div
          className="iris-card"
          style={{
            padding: 16,
            borderColor: counts?.groups_match ? "rgba(57,255,136,0.2)" : "rgba(255,153,51,0.2)",
            background: counts?.groups_match ? "rgba(57,255,136,0.03)" : "rgba(255,153,51,0.03)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            {counts?.groups_match ? (
              <CheckCircle2 size={14} style={{ color: "var(--iris-neon-green)" }} />
            ) : (
              <AlertTriangle size={14} style={{ color: "var(--iris-neon-orange)" }} />
            )}
            <Kicker variant="muted">Groups</Kicker>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            <div>
              <div className="iris-meta">Prod</div>
              <div className="iris-kpi" style={{ fontSize: 20, color: "var(--iris-env-prod)" }}>
                {counts?.prod_groups?.toLocaleString() ?? "\u2014"}
              </div>
            </div>
            <div>
              <div className="iris-meta">Dev</div>
              <div className="iris-kpi" style={{ fontSize: 20, color: "var(--iris-env-dev)" }}>
                {counts?.dev_groups?.toLocaleString() ?? "\u2014"}
              </div>
            </div>
          </div>
          {counts && !counts.groups_match && (
            <div style={{ marginTop: 8, fontSize: 11, color: "var(--iris-neon-orange)" }}>
              Delta: {counts.groups_delta > 0 ? "+" : ""}
              {counts.groups_delta}
            </div>
          )}
          {counts?.groups_match && (
            <div style={{ marginTop: 8, fontSize: 11, color: "var(--iris-neon-green)" }}>
              In sync
            </div>
          )}
        </div>

        {/* Entities card */}
        <div
          className="iris-card"
          style={{
            padding: 16,
            borderColor: counts?.entities_match ? "rgba(57,255,136,0.2)" : "rgba(255,153,51,0.2)",
            background: counts?.entities_match ? "rgba(57,255,136,0.03)" : "rgba(255,153,51,0.03)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            {counts?.entities_match ? (
              <CheckCircle2 size={14} style={{ color: "var(--iris-neon-green)" }} />
            ) : (
              <AlertTriangle size={14} style={{ color: "var(--iris-neon-orange)" }} />
            )}
            <Kicker variant="muted">Entities</Kicker>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            <div>
              <div className="iris-meta">Prod</div>
              <div className="iris-kpi" style={{ fontSize: 20, color: "var(--iris-env-prod)" }}>
                {counts?.prod_entities?.toLocaleString() ?? "\u2014"}
              </div>
            </div>
            <div>
              <div className="iris-meta">Dev</div>
              <div className="iris-kpi" style={{ fontSize: 20, color: "var(--iris-env-dev)" }}>
                {counts?.dev_entities?.toLocaleString() ?? "\u2014"}
              </div>
            </div>
          </div>
          {counts && !counts.entities_match && (
            <div style={{ marginTop: 8, fontSize: 11, color: "var(--iris-neon-orange)" }}>
              Delta: {counts.entities_delta > 0 ? "+" : ""}
              {counts.entities_delta}
            </div>
          )}
          {counts?.entities_match && (
            <div style={{ marginTop: 8, fontSize: 11, color: "var(--iris-neon-green)" }}>
              In sync
            </div>
          )}
        </div>
      </div>

      {/* Mismatch table */}
      {totalMismatches > 0 && (
        <div className="iris-card" style={{ padding: 0, overflow: "hidden" }}>
          <div
            style={{
              borderBottom: "1px solid var(--iris-line-1)",
              padding: "10px 18px",
            }}
          >
            <span style={{ fontSize: 11, fontWeight: 600, color: "var(--iris-neon-orange)" }}>
              {totalMismatches} mismatch{totalMismatches !== 1 ? "es" : ""} detected
            </span>
          </div>
          <DataTable
            columns={mismatchColumns}
            data={mismatches.slice(0, 50)}
            rowKey={(m) => `${m.type}-${m.scrapping_id}`}
          />
          {totalMismatches > 50 && (
            <div style={{ padding: "8px 18px", fontSize: 11, color: "var(--iris-fg-4)" }}>
              ... and {totalMismatches - 50} more
            </div>
          )}
        </div>
      )}

      {totalMismatches === 0 && counts && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            borderRadius: "var(--iris-r-md)",
            border: "1px solid rgba(57,255,136,0.2)",
            background: "rgba(57,255,136,0.05)",
            padding: "12px 16px",
            fontSize: 12,
            color: "var(--iris-neon-green)",
          }}
        >
          <CheckCircle2 size={14} />
          Both environments are in sync {"\u2014"} no mismatches found.
        </div>
      )}
    </div>
  );
}

// ─── Hygiene Issue Card ───

function IssueCard({
  issue,
  onFix,
}: { issue: HygieneIssue; onFix: (issue: HygieneIssue) => void }) {
  const variant = SEV_VARIANT[issue.severity as SeverityKey] ?? "idle";

  return (
    <div
      className="iris-card"
      style={{
        overflow: "hidden",
        borderColor:
          variant === "err"
            ? "rgba(255,77,210,0.2)"
            : variant === "warn"
              ? "rgba(255,153,51,0.2)"
              : undefined,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "12px 18px",
        }}
      >
        <StatusDot variant={variant} size={10} />
        <div style={{ flex: 1 }}>
          <span style={{ fontSize: 13, fontWeight: 500, color: "var(--iris-fg-1)" }}>
            {issue.type}
          </span>
          <p style={{ marginTop: 2, fontSize: 11, color: "var(--iris-fg-3)" }}>
            {issue.description}
          </p>
        </div>
        <StatusPill variant={variant}>{(issue.count ?? 0).toLocaleString()}</StatusPill>
        {issue.count > 0 && (
          <button
            type="button"
            className="iris-btn"
            onClick={() => onFix(issue)}
            style={{ flexShrink: 0 }}
          >
            Fix
          </button>
        )}
      </div>

      {/* Sample items */}
      {issue.items.slice(0, 5).map((item, i) => (
        <div
          key={item.id ?? item.ids?.join(",") ?? i}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            borderTop: "1px solid var(--iris-line-1)",
            background: "var(--iris-bg-3)",
            padding: "8px 18px",
            fontSize: 11,
          }}
        >
          <span
            style={{
              flex: 1,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              color: "var(--iris-fg-3)",
            }}
          >
            {item.label}
          </span>
          {item.detail && <span style={{ color: "var(--iris-fg-4)" }}>{item.detail}</span>}
        </div>
      ))}
      {issue.items.length > 5 && (
        <div
          style={{
            borderTop: "1px solid var(--iris-line-1)",
            padding: "8px 18px",
            fontSize: 11,
            color: "var(--iris-fg-4)",
          }}
        >
          ... and {issue.items.length - 5} more
        </div>
      )}
    </div>
  );
}

// ─── Main ───

export function HygieneEnvTab() {
  const { data: report, isFetching, refetch } = useHygieneReport();
  const fixHygiene = useFixHygiene();

  const issues = report?.issues ?? [];
  const scannedAt = report?.scanned_at;

  function handleFix(issue: HygieneIssue) {
    if (!confirm(`Auto-fix "${issue.type}"? This will apply the recommended remediation.`)) return;
    fixHygiene.mutate({
      type: issue.type,
      action: "auto_fix",
      items: issue.items.map((item) => ({ id: item.id, ids: item.ids })),
    });
  }

  const highCount = issues.filter((i) => i.severity === "high").length;
  const medCount = issues.filter((i) => i.severity === "medium").length;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* ── Environment Comparison ── */}
      <EnvComparisonSection />

      {/* Divider */}
      <div style={{ borderTop: "1px solid var(--iris-line-1)" }} />

      {/* ── Data Hygiene Report ── */}
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <Kicker variant="magenta">Data Hygiene</Kicker>
            {scannedAt && (
              <p className="iris-meta" style={{ marginTop: 4 }}>
                Last scanned: {new Date(scannedAt).toLocaleString()}
              </p>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {issues.length > 0 && (
              <div style={{ display: "flex", gap: 8 }}>
                {highCount > 0 && <StatusPill variant="err">{highCount} high</StatusPill>}
                {medCount > 0 && <StatusPill variant="warn">{medCount} medium</StatusPill>}
              </div>
            )}
            <button
              type="button"
              className="iris-btn"
              onClick={() => refetch()}
              disabled={isFetching}
              style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
            >
              <RefreshCw size={12} className={isFetching ? "iris-pulse" : ""} />
              {isFetching ? "Scanning..." : "Run Scan"}
            </button>
          </div>
        </div>

        {!report && !isFetching && (
          <div className="iris-card" style={{ padding: "24px 18px", textAlign: "center" }}>
            <p style={{ color: "var(--iris-fg-3)", fontSize: 12 }}>
              Click "Run Scan" to generate a hygiene report.
            </p>
          </div>
        )}

        {isFetching && (
          <div className="iris-card" style={{ padding: "24px 18px", textAlign: "center" }}>
            <p style={{ color: "var(--iris-fg-3)", fontSize: 12 }}>Scanning...</p>
          </div>
        )}

        {report && issues.length === 0 && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              borderRadius: "var(--iris-r-lg)",
              border: "1px solid rgba(57,255,136,0.2)",
              background: "rgba(57,255,136,0.05)",
              padding: "16px 18px",
              fontSize: 12,
              color: "var(--iris-neon-green)",
            }}
          >
            <CheckCircle2 size={16} />
            No hygiene issues detected. Database is clean.
          </div>
        )}

        {/* Issue cards — sorted high -> medium -> low */}
        {[...issues]
          .sort((a, b) => {
            const order: Record<string, number> = { high: 0, medium: 1, low: 2 };
            return (order[a.severity] ?? 3) - (order[b.severity] ?? 3);
          })
          .map((issue) => (
            <IssueCard key={issue.type} issue={issue} onFix={handleFix} />
          ))}
      </div>
    </div>
  );
}
